import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { getShopProductUrl } from '../../helpers/helpers';
import {
  getOpportunityLinkActions,
  getOpportunityLinkReason,
  getOpportunityLinkStateLabel,
  getParticipantsLabel,
  hasAgentPick
} from '../../helpers/url_data_agent_opportunities_helpers';
import { formatConfidence, formatPrice, getLiveMatchConfidenceBand, getProductAdminUrl, getUrlDataAdminUrl } from '../../helpers/url_data_agent_live_matches_helpers';
import { getUrlDataLink } from '../../helpers/url_data_helpers';
import PreviewThumb from './PreviewThumb';

import './OpportunityLinkRow.scss';

const SCOPE_NOTE = 'Needs the Match Product admin permission';

const OpportunityLinkRow = ({ link, isBusy, canMatchUrls = true, onAction }) => {
  // Display data
  const { page, product, evaluation } = link;
  const sourceUrl = getUrlDataLink(link);
  const urlDataUrl = getUrlDataAdminUrl(link);
  const sourceTitle = page.title || link.urlStem || link.sampleUrl || 'Unknown page';
  const agentPick = !product && hasAgentPick(link) ? evaluation.agentProduct : null;
  const shown = product || agentPick;
  const shownTitle = shown?.title || (!product && evaluation?.agentProductName) || (link.state === 'dismissed' ? 'Link dismissed' : 'No catalog product yet');
  const shownBrand = shown?.brand || (!product && evaluation?.agentBrandName) || null;
  const shownPrice = shown ? formatPrice(shown.fallbackPrice, shown.fallbackPriceCurrency) : null;
  const confidence = agentPick ? evaluation.overallConfidence : null;
  const reason = getOpportunityLinkReason(link);
  const actions = getOpportunityLinkActions(link);

  return (
    <div className={cn('opportunity-link-row', `state-${link.state}`)}>
      <PreviewThumb source={page.image} title={sourceTitle} subtitle={page.brand} />
      <div className='ol-text'>
        <div className='ol-title' title={sourceTitle}>
          {sourceUrl ? (
            <a href={sourceUrl} target='_blank' rel='noopener noreferrer'>
              {sourceTitle}
            </a>
          ) : (
            sourceTitle
          )}
        </div>
        <div className='ol-meta'>
          <span>{link.domain || 'page'}</span>
          <span>{getParticipantsLabel(link)}</span>
          {urlDataUrl && (
            <a href={urlDataUrl} target='_blank' rel='noopener noreferrer'>
              ↗ URL data
            </a>
          )}
        </div>
      </div>
      <span className='ol-glyph' aria-hidden='true'>
        ⇄
      </span>
      <PreviewThumb source={shown?.image || (!product && evaluation?.agentPrimaryImageUrl) || null} title={shownTitle} subtitle={shownBrand} />
      <div className='ol-text'>
        <div className='ol-title' title={shownTitle}>
          {shown ? (
            <a href={getShopProductUrl(shown.id)} target='_blank' rel='noopener noreferrer'>
              {shownTitle}
            </a>
          ) : (
            shownTitle
          )}
        </div>
        <div className='ol-meta'>
          {agentPick && <span className='ol-pick'>Agent’s pick</span>}
          {shownBrand && <span>{shownBrand}</span>}
          {shownPrice && <span>{shownPrice}</span>}
          {shown && (
            <a className='mono' href={getProductAdminUrl(shown.id)} target='_blank' rel='noopener noreferrer'>
              #{shown.id}
            </a>
          )}
          {confidence != null && <span className={cn('ol-conf', `band-${getLiveMatchConfidenceBand(confidence)}`)}>{formatConfidence(confidence)}</span>}
        </div>
      </div>
      <div className='ol-state'>
        <span className='ol-pill'>{getOpportunityLinkStateLabel(link)}</span>
        {reason && (
          <span className='ol-reason' title={reason}>
            {reason}
          </span>
        )}
      </div>
      <div className='ol-actions'>
        {actions.map(action => {
          const withheld = Boolean(action.scope) && !canMatchUrls;
          return (
            <button
              key={action.key}
              type='button'
              className={cn('se-btn', { primary: action.primary })}
              disabled={isBusy || withheld}
              title={withheld ? SCOPE_NOTE : undefined}
              onClick={() => onAction(action.key)}
            >
              {action.display}
            </button>
          );
        })}
      </div>
    </div>
  );
};

OpportunityLinkRow.propTypes = {
  link: PropTypes.object.isRequired,
  isBusy: PropTypes.bool,
  canMatchUrls: PropTypes.bool,
  onAction: PropTypes.func.isRequired
};

export default OpportunityLinkRow;
